import type { FormDataAdapter } from "$lib/util/model-adapters"
import { PatchBuilder } from "$lib/util/model-adapters/patch.ts"
import {
  type AccountOpeningApplication,
  RiskTolerance,
  TimeHorizon,
} from "@tastyworks/tastyworks-api"
import { type InferType, object, string } from "yup"

export const riskToleranceSchema = object({
  riskTolerance: string().oneOf(Object.values(RiskTolerance)).required(),
})

export const investmentTimeHorizonSchema = object({
  investmentTimeHorizon: string().oneOf(Object.values(TimeHorizon)).required(),
})

export const riskToleranceAdapter = {
  toForm: (application, formData) => {
    formData.riskTolerance = application.riskTolerance ?? undefined
    return formData
  },
  toPatch: (data) =>
    new PatchBuilder<AccountOpeningApplication>()
      .string("riskTolerance", data.riskTolerance as RiskTolerance)
      .build(),
} satisfies Pick<
  FormDataAdapter<
    AccountOpeningApplication,
    InferType<typeof riskToleranceSchema>
  >,
  "toForm" | "toPatch"
>

export const investmentTimeHorizonAdapter = {
  toForm: (application, formData) => {
    formData.investmentTimeHorizon =
      application.investmentTimeHorizon ?? undefined
    return formData
  },
  toPatch: (data) =>
    new PatchBuilder<AccountOpeningApplication>()
      .string(
        "investmentTimeHorizon",
        data.investmentTimeHorizon as TimeHorizon
      )
      .build(),
} satisfies Pick<
  FormDataAdapter<
    AccountOpeningApplication,
    InferType<typeof investmentTimeHorizonSchema>
  >,
  "toForm" | "toPatch"
>

export const businessPreferencesSchema = object({
  riskTolerance: riskToleranceSchema,
  investmentTimeHorizon: investmentTimeHorizonSchema,
})

if (import.meta.vitest) {
  const { it, expect, describe } = import.meta.vitest

  describe("businessPreferencesSchema", () => {
    describe("riskToleranceSchema", () => {
      it("validates valid risk tolerance values", () => {
        expect(
          riskToleranceSchema.isValidSync({
            riskTolerance: RiskTolerance.LOW,
          })
        ).toBe(true)
      })

      it("rejects invalid values", () => {
        expect(
          riskToleranceSchema.isValidSync({ riskTolerance: "invalid" })
        ).toBe(false)
      })

      it("requires riskTolerance field", () => {
        expect(riskToleranceSchema.isValidSync({})).toBe(false)
      })
    })

    describe("investmentTimeHorizonSchema", () => {
      it("validates valid time horizon values", () => {
        expect(
          investmentTimeHorizonSchema.isValidSync({
            investmentTimeHorizon: TimeHorizon.SHORT,
          })
        ).toBe(true)
      })

      it("rejects invalid values", () => {
        expect(
          investmentTimeHorizonSchema.isValidSync({
            investmentTimeHorizon: "invalid",
          })
        ).toBe(false)
      })

      it("requires investmentTimeHorizon field", () => {
        expect(investmentTimeHorizonSchema.isValidSync({})).toBe(false)
      })
    })

    describe("businessPreferencesSchema", () => {
      it("validates complete business preferences", () => {
        expect(
          businessPreferencesSchema.isValidSync({
            riskTolerance: {
              riskTolerance: RiskTolerance.MEDIUM,
            },
            investmentTimeHorizon: {
              investmentTimeHorizon: TimeHorizon.AVERAGE,
            },
          })
        ).toBe(true)
      })

      it("rejects incomplete data", () => {
        expect(
          businessPreferencesSchema.isValidSync({
            riskTolerance: {
              riskTolerance: RiskTolerance.MEDIUM,
            },
          })
        ).toBe(false)
      })
    })
  })

  describe("riskToleranceAdapter", () => {
    it("fills the form from the application", async () => {
      const { accountOpeningApplicationFactory } =
        await import("@tastyworks/tastyworks-api/factories/account-opening-application")
      const application = accountOpeningApplicationFactory.build({
        riskTolerance: RiskTolerance.LOW,
      })

      expect(riskToleranceAdapter.toForm(application, {})).toEqual({
        riskTolerance: RiskTolerance.LOW,
      })
    })

    it("patches the risk tolerance", () => {
      expect(
        riskToleranceAdapter.toPatch({ riskTolerance: RiskTolerance.LOW })
      ).toEqual({ riskTolerance: RiskTolerance.LOW })
    })
  })

  describe("investmentTimeHorizonAdapter", () => {
    it("fills the form from the application", async () => {
      const { accountOpeningApplicationFactory } =
        await import("@tastyworks/tastyworks-api/factories/account-opening-application")
      const application = accountOpeningApplicationFactory.build({
        investmentTimeHorizon: TimeHorizon.SHORT,
      })

      expect(investmentTimeHorizonAdapter.toForm(application, {})).toEqual({
        investmentTimeHorizon: TimeHorizon.SHORT,
      })
    })

    it("patches the investment time horizon", () => {
      expect(
        investmentTimeHorizonAdapter.toPatch({
          investmentTimeHorizon: TimeHorizon.SHORT,
        })
      ).toEqual({ investmentTimeHorizon: TimeHorizon.SHORT })
    })
  })
}
